import { GAME_CONSTANTS, } from '@tankworld/shared';
import { BattlefieldTerrain } from './terrain.js';
import { CollisionSystem } from './collision.js';
import { VehiclePhysics } from './vehicle.js';
import { ProjectileSystem } from './projectiles.js';
import { BotController } from './bot.js';
export class SimulationWorld {
    terrain;
    collisionSystem;
    projectileSystem;
    vehicles = new Map();
    bots = new Map();
    obstacles = [];
    pendingInputs = new Map();
    pendingFireRequests = new Set();
    combatEvents = [];
    tick = 0;
    simulationTime = 0;
    constructor() {
        this.terrain = new BattlefieldTerrain();
        this.collisionSystem = new CollisionSystem(this.terrain);
        this.projectileSystem = new ProjectileSystem(this.collisionSystem);
        this.obstacles = this.terrain.getStaticObstacles();
    }
    addVehicle(id, name, color = '#4b5320', isBot = false, spawnPos, initialYaw = 0, tankClassId = 'mbt_standard') {
        let pos = spawnPos;
        if (!pos) {
            // Pick default safe position in arena
            const count = this.vehicles.size;
            const angle = (count / 8) * Math.PI * 2;
            const radius = 60;
            const x = Math.sin(angle) * radius;
            const z = Math.cos(angle) * radius;
            pos = { x, y: this.terrain.getHeight(x, z) + 0.35, z };
            initialYaw = angle + Math.PI;
        }
        const vehicle = new VehiclePhysics(id, name, color, isBot, pos, initialYaw, tankClassId);
        this.vehicles.set(id, vehicle);
        return vehicle;
    }
    addBot(id, name, difficulty = 'normal', color = '#8b0000') {
        const angle = (this.vehicles.size / 8) * Math.PI * 2 + Math.PI * 0.5;
        const radius = 70 + Math.random() * 20;
        const x = Math.sin(angle) * radius;
        const z = Math.cos(angle) * radius;
        const pos = { x, y: this.terrain.getHeight(x, z) + 0.35, z };
        const vehicle = this.addVehicle(id, name, color, true, pos, angle + Math.PI);
        const bot = new BotController(vehicle, difficulty);
        this.bots.set(id, bot);
        return vehicle;
    }
    removeVehicle(id) {
        this.vehicles.delete(id);
        this.bots.delete(id);
        this.pendingInputs.delete(id);
        this.pendingFireRequests.delete(id);
    }
    queueInput(id, input) {
        this.pendingInputs.set(id, input);
    }
    queueFireRequest(id) {
        this.pendingFireRequests.add(id);
    }
    /**
     * Main fixed-timestep step
     */
    step(dt = GAME_CONSTANTS.FIXED_DELTA_TIME) {
        this.tick++;
        this.simulationTime += dt;
        const stepEvents = [];
        // 1. Update AI bots to produce inputs and fire requests
        const vehicleList = Array.from(this.vehicles.values());
        for (const [botId, bot] of this.bots.entries()) {
            const { input, wantsToFire } = bot.update(dt, vehicleList, this.obstacles, this.terrain);
            this.pendingInputs.set(botId, input);
            if (wantsToFire) {
                this.pendingFireRequests.add(botId);
            }
        }
        // 2. Update all vehicle kinematics & suspension
        for (const [id, vehicle] of this.vehicles.entries()) {
            const input = this.pendingInputs.get(id) || {
                sequence: 0,
                throttle: 0,
                steer: 0,
                brake: true,
                turretYaw: vehicle.hullYaw,
                gunPitch: 0,
            };
            vehicle.update(dt, input, this.terrain);
            // Resolve obstacle collisions
            this.collisionSystem.resolveObstacleCollisions(vehicle.position, GAME_CONSTANTS.TANK_WIDTH * 0.55, this.obstacles);
            // Arena boundary clamping
            this.collisionSystem.clampToArena(vehicle.position, GAME_CONSTANTS.TANK_WIDTH * 0.55);
        }
        // 3. Resolve tank-vs-tank collisions
        const vehicleArray = Array.from(this.vehicles.values());
        for (let i = 0; i < vehicleArray.length; i++) {
            for (let j = i + 1; j < vehicleArray.length; j++) {
                const vA = vehicleArray[i];
                const vB = vehicleArray[j];
                if (vA.isAlive && vB.isAlive) {
                    this.collisionSystem.resolveTankVsTank(vA.position, vB.position, GAME_CONSTANTS.TANK_WIDTH * 0.55);
                }
            }
        }
        // 4. Handle fire requests
        for (const id of this.pendingFireRequests) {
            const vehicle = this.vehicles.get(id);
            if (vehicle && vehicle.canFire()) {
                const { muzzlePos, muzzleDir } = vehicle.onFire();
                this.projectileSystem.spawn(id, muzzlePos, muzzleDir, vehicle.velocity, vehicle.selectedAmmoId || 'ap_3bm42');
                stepEvents.push({
                    id: `evt-fire-${this.tick}-${id}`,
                    type: 'fire',
                    attackerId: id,
                    attackerName: vehicle.name,
                    position: muzzlePos,
                    timestamp: Date.now(),
                });
            }
        }
        this.pendingFireRequests.clear();
        // 5. Update projectiles and ballistics
        this.projectileSystem.update(dt, this.obstacles, this.vehicles, stepEvents);
        // Export snapshots
        const tankStates = Array.from(this.vehicles.values()).map(v => v.toState());
        const projectileStates = this.projectileSystem.toStates();
        return {
            tanks: tankStates,
            projectiles: projectileStates,
            events: stepEvents,
        };
    }
    reset() {
        this.tick = 0;
        this.simulationTime = 0;
        this.projectileSystem.clear();
        this.pendingInputs.clear();
        this.pendingFireRequests.clear();
        this.combatEvents = [];
        for (const v of this.vehicles.values()) {
            v.respawn(this.terrain);
        }
    }
}
//# sourceMappingURL=world.js.map